import ChannelContext from '@jetshop/core/components/ChannelContext';
import { constructGallery } from '@jetshop/ui/Gallery/constructGallery';
import Image from '@jetshop/ui/Image/Image';
import { styled } from 'linaria/react';
import React from 'react';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import { ReactComponent as PlayCircle } from '../../../svg/PlayCircle.svg';
import { theme } from '../../Theme';
import { Iframes } from './Iframe';

const BaseWrapper = styled('div')`
  position: relative;
  img {
    max-height: 100vh;
  }
  .image-gallery-image {
    text-align: center;
  }
  .image-gallery-thumbnail-inner {
    font-size: 10px;
  }
  .image-gallery-right-nav,
  .image-gallery-left-nav,
  .image-gallery-fullscreen-button {
    font-size: 2rem;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
    height: 2.5rem;
    width: 2.5rem;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.4);
    transition: background 0.3s ease-in-out;
    margin: 0 0.5rem;
    &:hover {
      background: rgba(255, 255, 255, 0.8);
    }
    &:before {
      color: black;
      text-shadow: none;
    }
  }
  .image-gallery.fullscreen-modal {
    z-index: 1000;
  }
  .image-gallery-fullscreen-button {
    margin-bottom: 0.5rem;
  }
  .image-gallery-fullscreen-button:before {
    text-shadow: none;
    font-size: 0.7em;
    padding: 0;
  }
  .image-gallery-thumbnail.active {
    border-width: 1px;
  }
  .image-gallery-thumbnails-wrapper.left .image-gallery-thumbnails,
  .image-gallery-thumbnails-wrapper.right .image-gallery-thumbnails {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  }
  .fullscreen {
    .image-gallery-fullscreen-button {
      margin: 2rem;
    }
    .image-gallery-right-nav,
    .image-gallery-left-nav {
      margin: 0 2rem;
    }
    img {
      width: auto;
      max-width: 100%;
    }
    .image-gallery-thumbnails-wrapper {
      display: none;
    }
  }
`;

const Wrapper = styled(BaseWrapper)`
  display: flex;
  flex-direction: column;
  .image-gallery-thumbnails-wrapper.right .image-gallery-thumbnails {
    justify-content: unset !important;
  }

  .image-gallery-thumbnail.active {
    border: 1px solid transparent;
  }

  .image-gallery-fullscreen-button:hover {
    background: #1a1a1a;
  }

  ${theme.below.md} {
    .image-gallery-content.fullscreen {
      img {
        width: 100% !important;
      }
      .image-gallery-slide-wrapper.left,
      .image-gallery-slide-wrapper.right {
        width: 100%;
      }
    }
  }
`;

const PlayIcon = styled('div')`
  height: 30px;
  width: 30px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 0;
  right: 0;
  margin: auto;
  background: transparent;
  z-index: 1;
  svg {
    height: 30px;
    width: 30px;
  }
  + div {
    opacity: 0.5;
  }
`;

const thumbnailSizes = ['5rem'];
const aspect = '2:3';

function setVideoType(link) {
  switch (true) {
    case link.includes('youtube'): {
      return 'Youtube';
    }
    case link.includes('vimeo'): {
      return 'Vimeo';
    }
    case link.includes('pub_docs'): {
      return 'Internal';
    }
    default:
      return 'Internal';
  }
}

export default function Gallery({ images, children, className, video }) {
  const [isFullscreen, setFullscreen] = React.useState(false);
  const { selectedChannel } = React.useContext(ChannelContext);

  const galleryImages = React.useMemo(() => {
    let constructedImages = constructGallery(
      images,
      [1],
      '2:3',
      selectedChannel.url,
      []
    ).map(image => ({
      item: {
        ...image
      },
      video: {
        isVideo: false,
        original: ''
      }
    }));

    return constructedImages;
  }, [images, selectedChannel.url]);

  galleryImages.splice(
    1,
    0,
    video && {
      item: {
        original: images[0].url,
        thumbnail: images[0].url
      },
      video: {
        isVideo: video.length > 0,
        original: video,
        videoType: setVideoType(video)
      }
    }
  );

  return (
    <Wrapper className={className}>
      <ImageGallery
        items={galleryImages.filter(Boolean)}
        autoPlay={false}
        showPlayButton={false}
        thumbnailPosition="left"
        thumbnailSizes={thumbnailSizes}
        showThumbnails={images.length > 1}
        aspect={aspect}
        sizes={[1, 1, 1 / 2, 1 / 2]}
        fullScreenSizes={[1, 1, 1, 1]}
        useBrowserFullscreen={false}
        preventDefaultTouchmoveEvent={isFullscreen}
        onScreenChange={el => setFullscreen(!!el)}
        renderItem={({ item, video }) => {
          if (video?.isVideo) {
            return (
              <div className="image-gallery-image">
                {Iframes[video.videoType](video.original)}
              </div>
            );
          } else {
            return (
              <div className="image-gallery-image">
                {isFullscreen ? (
                  <img
                    src={item?.original}
                    alt={item?.originalAlt}
                    srcSet={item?.srcSet}
                    sizes={item?.sizes}
                    title={item?.originalTitle}
                  />
                ) : (
                  <Image
                    src={item?.original}
                    alt={item?.originalAlt}
                    sizes={[1, 1, 1 / 2, 1 / 2]}
                    title={item?.originalTitle}
                    aspect={aspect}
                  />
                )}
              </div>
            );
          }
        }}
        renderThumbInner={({ item, video }) => {
          return (
            <div className="image-gallery-thumbnail-inner">
              {video?.isVideo && (
                <PlayIcon>
                  <PlayCircle />
                </PlayIcon>
              )}
              <Image
                src={item?.thumbnail}
                alt={item?.thumbnailAlt}
                title={item?.thumbnailTitle}
                sizes={thumbnailSizes}
                aspect={aspect}
              />
              {item?.thumbnailLabel && (
                <div className="image-gallery-thumbnail-label">
                  {item?.thumbnailLabel}
                </div>
              )}
            </div>
          );
        }}
      ></ImageGallery>
      {children}
    </Wrapper>
  );
}
